import { useState } from 'react';
import { App, Button, Spin, Upload } from 'antd';
import type { UploadProps } from 'antd';
import { Trash, UploadSimple } from '@phosphor-icons/react';
import { ApiError, NetworkError } from '../api/client';
import { mediaApi } from '../api/media';
import { tokens } from '../theme';

/**
 * Trần dung lượng phía client — **chỉ là gợi ý** cho người dùng, không phải chốt chặn: server
 * (`PublicMediaImage.MaxBytes`) mới quyết định. Để ở MỘT chỗ này nên sửa một lần là cả ba form
 * (chuỗi / nội dung / quà) đổi theo, vì cả ba dùng chung widget này.
 */
export const MAX_IMAGE_MB = 10;

interface Props {
  /**
   * AntD `Form.Item` truyền vào — URL ảnh hiện tại (chuỗi). Cột lưu vẫn là chuỗi URL nên ảnh đã
   * lưu từ trước (kể cả URL dán tay đời cũ) vẫn hiện trong ô và vẫn giữ nguyên khi lưu lại.
   */
  value?: string | null;
  onChange?: (value: string | null) => void;
}

/**
 * Ô ảnh dùng chung cho ba form Admin (`S7-BE-06`): **một vùng kéo–thả duy nhất** cho mỗi ảnh
 * (ví dụ 1 ô ảnh bìa + 1 ô logo).
 *
 * <p>Chỉ nhận tải tệp — không có ô dán URL tay. Khi trống thì ô hiện lời mời kéo–thả; sau khi thả
 * (hoặc khi mở ra sửa mà đã có ảnh) thì **chính ô đó hiện ảnh**, kéo–thả/bấm để đổi, nút Xóa để bỏ.
 * Ảnh đã lưu từ trước vẫn hiện + giữ nguyên khi lưu lại — tương thích ngược ở tầng dữ liệu.</p>
 */
export function ImageUploadField({ value, onChange }: Props) {
  const { message } = App.useApp();
  const [uploading, setUploading] = useState(false);

  const customRequest: UploadProps['customRequest'] = async ({ file }) => {
    const picked = file as File;

    // Chặn sớm ở client cho đỡ tải lên vô ích; server vẫn kiểm lại (không tin client).
    if (picked.size > MAX_IMAGE_MB * 1024 * 1024) {
      message.error(`Ảnh lớn hơn ${MAX_IMAGE_MB}MB. Chọn ảnh nhẹ hơn hoặc giảm độ phân giải.`);
      return;
    }

    setUploading(true);
    try {
      const uploaded = await mediaApi.uploadImage(picked);
      onChange?.(uploaded.url);
      message.success('Đã tải ảnh lên.');
    } catch (error) {
      message.error(
        error instanceof ApiError || error instanceof NetworkError
          ? error.message
          : 'Tải ảnh thất bại. Thử lại.',
      );
    } finally {
      setUploading(false);
    }
  };

  const content = () => {
    if (uploading) {
      return (
        <div style={{ padding: '16px 0' }}>
          <Spin />
          <div style={{ marginTop: 8, fontSize: 13, color: tokens.color.ink2 }}>Đang tải ảnh…</div>
        </div>
      );
    }

    // Đã có ảnh: hiện thẳng trong ô kéo–thả. Kéo–thả/bấm để đổi, nút Xóa để bỏ.
    if (value) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <img
            src={value}
            alt="Ảnh đã chọn"
            style={{ maxHeight: 160, maxWidth: '100%', objectFit: 'contain', borderRadius: tokens.radius.card }}
            // URL cũ chết không nên làm vỡ layout — ẩn ảnh, phần chữ dưới vẫn cho đổi/xóa.
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <div style={{ fontSize: 12, color: tokens.color.ink3 }}>
            Kéo–thả ảnh khác vào đây, hoặc bấm để đổi
          </div>
          <Button
            size="small"
            danger
            icon={<Trash size={14} aria-hidden />}
            // Bấm Xóa KHÔNG được mở hộp chọn tệp của vùng kéo–thả — chặn nổi bọt sự kiện.
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(null);
            }}
          >
            Xóa ảnh
          </Button>
        </div>
      );
    }

    return (
      <>
        <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
          <UploadSimple size={28} aria-hidden />
        </p>
        <p className="ant-upload-text" style={{ fontSize: 14 }}>
          Kéo–thả ảnh vào đây, hoặc bấm để chọn
        </p>
        <p className="ant-upload-hint" style={{ fontSize: 12, color: tokens.color.ink3 }}>
          JPG, PNG, WEBP · tối đa {MAX_IMAGE_MB}MB
        </p>
      </>
    );
  };

  // MỘT vùng kéo–thả duy nhất cho mỗi ảnh — trống hay đã có ảnh cũng là cùng cái ô.
  return (
    <Upload.Dragger
      accept="image/*"
      showUploadList={false}
      multiple={false}
      maxCount={1}
      disabled={uploading}
      customRequest={customRequest}
    >
      {content()}
    </Upload.Dragger>
  );
}
